import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  VStack,
  Box,
  HStack,
  Text,
  SimpleGrid,
  Icon,
  Button,
  Badge,
  useDisclosure,
} from '@chakra-ui/react';
import { toaster } from '@/components/ui/toaster'
import { FiCheck, FiCircle, FiBookOpen, FiMapPin, FiZap, FiScissors, FiPlay, FiPlus, FiMap } from 'react-icons/fi'
import type { CampaignDetailDto, CampaignTurfRowDto } from '../../../types/campaign'
import { useUpdateCampaign, useCampaignTurfs, useDailyReview } from '../../../hooks/useCampaign'
import { useIsMobile } from '../../../hooks/useIsMobile'
import { StickyActionFooter } from '../../shared'
import { CampaignScriptCard } from '../CampaignScriptCard'
import { CampaignAutoGenerateModal } from '../CampaignAutoGenerateModal'
import { MergeListModal } from '../MergeListModal'
import { TurfReviewMapModal } from '../dailyReview/TurfReviewMapModal'
import { TurfMapViewerDrawer } from '../TurfMapViewerDrawer'
import { ToolCard } from './ToolCard'

interface Props {
  campaign: CampaignDetailDto
}

function todayStr(): string {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

export function DraftSetupView({ campaign }: Props) {
  const navigate = useNavigate()
  const isMobile = useIsMobile()
  const updateMutation = useUpdateCampaign(campaign.id)
  const { data: turfs } = useCampaignTurfs(campaign.id)
  const { data: review } = useDailyReview(campaign.id, todayStr())
  const autoGenerateModal = useDisclosure()
  const mergeListModal = useDisclosure()
  const recutModal = useDisclosure()
  const turfMapDrawer = useDisclosure()
  const [viewTurfMapId, setViewTurfMapId] = useState<string | null>(null)

  const hasScript = campaign.hasScript
  const hasTurfMap = campaign.hasTurfMap
  const canActivate = hasScript && hasTurfMap
  const hasTurfs = (turfs?.length ?? 0) > 0

  const handleActivate = async () => {
    try {
      await updateMutation.mutateAsync({
        label: campaign.label,
        description: campaign.description ?? undefined,
        status: 'Active',
        refreshStrategyOverride: campaign.refreshStrategyOverride as any,
      })
      toaster.create({ title: 'Campaign activated', type: 'success', duration: 3000 })
    } catch {
      toaster.create({ title: 'Failed to activate campaign', type: 'error', duration: 5000 })
    }
  }

  const handleMerged = (_turfMapId: string) => {
    recutModal.onOpen()
  }

  return (
    <VStack gap={6} align="stretch">
      {/* Activation Checklist */}
      <Box bg="surface" p={6} borderRadius="lg" shadow="md">
        <Text fontWeight="medium" mb={4}>Setup Checklist</Text>
        <VStack gap={3} align="stretch">
          <ChecklistItem done label="Campaign created" />
          <ChecklistItem
            done={hasScript}
            label="Assign a canvassing script"
            hint="Configure the default script below"
          />
          <ChecklistItem
            done={hasTurfMap}
            label="Create turfs"
            hint="Use 'Cut Turfs' or 'Auto-Turf' below"
          />
        </VStack>
        {!isMobile && (
          <Button
            mt={5}
            colorPalette="green"
            disabled={!canActivate}
            loading={updateMutation.isPending}
            onClick={handleActivate}><FiPlay />Activate Campaign
          </Button>
        )}
        {!canActivate && (
          <Text fontSize="xs" color="fg.muted" mt={2}>
            Complete all checklist items to activate.
          </Text>
        )}
      </Box>

      {/* Script Config */}
      <CampaignScriptCard campaignId={campaign.id} />

      {/* Turf Management */}
      <Box bg="surface" p={6} borderRadius="lg" shadow="md">
        <HStack justify="space-between" mb={4}>
          <HStack gap={3}>
            <Text fontWeight="medium">Turfs</Text>
            {campaign.activeTurfMapId && (
              <Button
                size="xs"
                variant="ghost"
                colorPalette="brand"
                onClick={() => {
                  setViewTurfMapId(campaign.activeTurfMapId!)
                  turfMapDrawer.onOpen()
                }}><FiMap />View Map
                              </Button>
            )}
          </HStack>
          <HStack gap={2}>
            {hasTurfs ? (
              <>
                <Button
                  size="sm"
                  colorPalette="brand"
                  variant="outline"
                  onClick={mergeListModal.onOpen}><FiPlus />Add Contacts
                                  </Button>
                <Button size="sm" colorPalette="brand" onClick={recutModal.onOpen}><FiMap />Review and Update Turfs
                                  </Button>
              </>
            ) : (
              <>
                <Button
                  size="sm"
                  colorPalette="brand"
                  variant="outline"
                  onClick={() => navigate(`/dashboard/turf-maps/new?campaignId=${campaign.id}`)}><FiScissors />Cut Turfs
                                  </Button>
                <Button size="sm" colorPalette="brand" onClick={autoGenerateModal.onOpen}><FiZap />Auto-Turf
                                  </Button>
              </>
            )}
          </HStack>
        </HStack>

        {!hasTurfs ? (
          <Box p={8} bg="gray.50" borderRadius="md" textAlign="center">
            <Text color="gray.500">
              No turfs yet. Use "Cut Turfs" or "Auto-Turf" to create turfs for this campaign.
            </Text>
          </Box>
        ) : (
          <DraftTurfsList turfs={turfs!} />
        )}
      </Box>

      {/* Campaign Tools */}
      <Box bg="surface" p={6} borderRadius="lg" shadow="md">
        <Text fontWeight="medium" mb={4}>Campaign Tools</Text>
        <SimpleGrid columns={{ base: 2, md: 3 }} gap={3}>
          <ToolCard icon={FiBookOpen} label="Scripts" description="Build canvassing scripts" to="/dashboard/canvassing/scripts" />
        </SimpleGrid>
      </Box>

      {/* Modals */}
      <CampaignAutoGenerateModal
        isOpen={autoGenerateModal.open}
        onClose={autoGenerateModal.onClose}
        campaignId={campaign.id}
        campaignLabel={campaign.label}
        onSuccess={() => {}}
      />
      {campaign.activeTurfMapId && (
        <MergeListModal
          isOpen={mergeListModal.open}
          onClose={mergeListModal.onClose}
          turfMapId={campaign.activeTurfMapId}
          campaignLabel={campaign.label}
          onMerged={handleMerged}
        />
      )}
      {review && (
        <TurfReviewMapModal
          isOpen={recutModal.open}
          onClose={recutModal.onClose}
          campaignId={campaign.id}
          turfMaps={review.turfMaps}
          turfCoverage={review.turfCoverage}
          reviewDate={todayStr()}
          assignmentMode={campaign.assignmentMode}
        />
      )}
      <TurfMapViewerDrawer
        isOpen={turfMapDrawer.open}
        onClose={() => { turfMapDrawer.onClose(); setViewTurfMapId(null) }}
        turfMapId={viewTurfMapId}
      />

      {isMobile && (
        <StickyActionFooter>
          <Button
            colorPalette="green"
            disabled={!canActivate}
            loading={updateMutation.isPending}
            onClick={handleActivate}><FiPlay />Activate Campaign
          </Button>
        </StickyActionFooter>
      )}
    </VStack>
  );
}

function ChecklistItem({ done, label, hint }: { done: boolean; label: string; hint?: string }) {
  return (
    <HStack gap={3}>
      <Icon
        as={done ? FiCheck : FiCircle}
        color={done ? 'green.500' : 'gray.300'}
        boxSize={4}
      />
      <Box>
        <Text fontSize="sm" color={done ? 'gray.700' : 'gray.500'} fontWeight={done ? 'medium' : 'normal'}>
          {label}
        </Text>
        {hint && !done && (
          <Text fontSize="xs" color="gray.400">{hint}</Text>
        )}
      </Box>
    </HStack>
  );
}

function DraftTurfsList({ turfs }: { turfs: CampaignTurfRowDto[] }) {
  return (
    <VStack gap={2} align="stretch">
      {turfs.map(t => (
        <HStack
          key={t.turfId}
          p={3}
          bg="gray.50"
          borderRadius="md"
          justify="space-between"
        >
          <HStack gap={2}>
            <Icon as={FiMapPin} color="accent" boxSize={4} />
            <Text fontSize="sm" fontWeight="medium">{t.turfLabel}</Text>
          </HStack>
          <Badge bg="info.subtle" color="info.fg">{t.memberCount} contacts</Badge>
        </HStack>
      ))}
    </VStack>
  );
}
